import { LOCAL_PACKAGE, LOCAL_SEMANTIC_MODEL, VISUAL_MODEL, QUERYABLE_MODEL, RDFS_MODEL } from "../../model/known-models.js";
import { createRdfsModel, createSgovModel } from "../../semantic-model/simplified/index.js";
import { createInMemorySemanticModel } from "../../semantic-model/simplified/in-memory-semantic-model.js";
import { createVisualModel } from "../../semantic-model/simplified/visual-model.js";
import { PimStoreWrapper } from "../../semantic-model/v1-adapters/index.js";
/**
 * Implementation of PackageService that communicates with backend and provides semantic models.
 */
export class BackendPackageService {
    backendUrl;
    httpFetch;
    constructor(backendUrl, httpFetch) {
        this.backendUrl = backendUrl;
        this.httpFetch = httpFetch;
    }
    async getResource(resourceId) {
        const result = await this.httpFetch(this.getResourceUrl(resourceId));
        return (await result.json());
    }
    async getPackage(packageId) {
        const result = await this.httpFetch(this.getPackageUrl(packageId));
        if (result.status === 404) {
            // @ts-ignore
            return null;
        }
        return (await result.json());
    }
    async createResource(parentPackageId, data) {
        const result = await this.httpFetch(this.getResourceUrl(parentPackageId, true), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });
        return (await result.json());
    }
    async createPackage(parentPackageId, data) {
        const result = await this.httpFetch(this.getPackageUrl(parentPackageId, true), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });
        return (await result.json());
    }
    async updatePackage(packageId, data) {
        const result = await this.httpFetch(this.getPackageUrl(packageId), {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });
        return (await result.json());
    }
    async deletePackage(packageId) {
        await this.httpFetch(this.getPackageUrl(packageId), {
            method: "DELETE",
        });
    }
    async deleteResource(iri) {
        await this.httpFetch(this.getResourceUrl(iri), {
            method: "DELETE",
        });
    }
    async applyTransactions(projectId, transactions) {
        const response = await this.httpFetch(this.getTransactionsUrl(projectId, "/apply"), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ transactions }),
        });
        if (response.status < 200 || response.status >= 300) {
            throw new Error(`Failed to apply transactions on the backend, status ${response.status}.`);
        }
    }
    async getResourceJsonData(id, blobId) {
        const result = await this.httpFetch(this.getBlobUrl(id, blobId));
        return (result.status >= 200 && result.status < 300) ? await result.json() : null;
    }
    async setResourceJsonData(id, data, blobId) {
        await this.httpFetch(this.getBlobUrl(id, blobId), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
        });
    }
    /**
     * This method is used by CME editor for transparently loading all models from a given package.
     * @todo It should not be a package, but a model itself with all its dependencies. Now it is a model.
     * @param packageId
     * @returns
     */
    async constructSemanticModelPackageModels(packageId) {
        const entityModels = [];
        const visualModels = [];
        const recursivellyLoadPackage = async (packageId) => {
            const pckg = await this.getPackage(packageId);
            const [entity, visual] = await this.getModelsFromResources(pckg.subResources);
            entityModels.push(...entity);
            visualModels.push(...visual);
            for (const resource of pckg.subResources) {
                if (resource.types.includes(LOCAL_PACKAGE)) {
                    await recursivellyLoadPackage(resource.iri);
                }
            }
        };
        await recursivellyLoadPackage(packageId);
        return [entityModels, visualModels];
    }
    /**
     * Simplified operation for updating a list of models on backend.
     *
     * If some models (semantic or visual) are ommited, they will be deleted.
     */
    async updateSemanticModelPackageModels(packageId, models, visualModels) {
        const responseStatuses = new Set();
        for (const visualModel of [...models, ...visualModels]) {
            // @ts-ignore
            const modelSerialization = visualModel.serializeModel();
            const iri = visualModel.getId();
            const semanticLabel = modelSerialization.type === LOCAL_SEMANTIC_MODEL
                ? modelSerialization.label
                : undefined;
            const name = semanticLabel
                ? Object.values(semanticLabel)[0]
                : modelSerialization.modelAlias ?? modelSerialization.alias;
            if (modelSerialization.type === RDFS_MODEL) {
                modelSerialization.label = { en: name };
                delete modelSerialization.modelAlias;
                delete modelSerialization.alias;
            }
            const response = await this.httpFetch(this.getResourceUrl(iri));
            const userMetadata = semanticLabel
                ? { label: semanticLabel }
                : name ? { label: { en: name } } : {};
            if (response.status !== 200) {
                const createdResponse = await this.httpFetch(this.getResourceUrl(packageId, true), {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        iri: iri,
                        type: modelSerialization.type,
                        userMetadata: userMetadata
                    }),
                });
                responseStatuses.add(createdResponse.status);
            }
            else {
                const updatedResponse = await this.httpFetch(this.getResourceUrl(iri), {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        userMetadata: userMetadata
                    }),
                });
                responseStatuses.add(updatedResponse.status);
            }
            const updatedResponse = await this.httpFetch(this.getBlobUrl(iri), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(modelSerialization),
            });
            responseStatuses.add(updatedResponse.status);
        }
        // Remove other models.
        //
        // An empty `models`/`visualModels` is never a legitimate "the user
        // removed every model" - a package always has at least its own
        // primary semantic model - so it can only mean the caller failed to
        // load its models before saving. Deleting on that basis would wipe
        // every model resource in the package, so skip the cleanup instead.
        const modelIds = [...models, ...visualModels].map(model => model.getId());
        if (modelIds.length > 0) {
            const pckg = await this.getPackage(packageId);
            for (const model of pckg.subResources) {
                if (model.types.some(t => [
                    VISUAL_MODEL,
                    QUERYABLE_MODEL,
                    LOCAL_SEMANTIC_MODEL,
                    RDFS_MODEL
                ].includes(t))) {
                    if (!modelIds.includes(model.iri)) {
                        // Remove model
                        await this.deleteResource(model.iri);
                    }
                }
            }
        }
        const anyErrors = [...responseStatuses.values()].filter((n) => n > 399).length;
        if (anyErrors > 0) {
            return false;
        }
        return true;
        // return null as any as Package; // todo
    }
    /**
     * Performs only update of a single model. It will not delete any other models.
     */
    async updateSingleModel(model) {
        // @ts-ignore
        const modelSerialization = model.serializeModel();
        const iri = model.getId();
        await this.httpFetch(this.getBlobUrl(iri), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(modelSerialization),
        });
    }
    getPackageUrl(packageId, asParent = false) {
        let url = this.backendUrl + "/resources/packages";
        url += "?" + (asParent ? "parentIri" : "iri") + "=" + encodeURIComponent(packageId);
        return url;
    }
    getResourceUrl(iri, asParent = false) {
        let url = this.backendUrl + "/resources";
        url += "?" + (asParent ? "parentIri" : "iri") + "=" + encodeURIComponent(iri);
        return url;
    }
    getBlobUrl(iri, name) {
        let url = this.backendUrl + "/resources/blob";
        url += "?" + "iri" + "=" + encodeURIComponent(iri);
        if (name) {
            url += "&name=" + encodeURIComponent(name);
        }
        return url;
    }
    getTransactionsUrl(projectIri, suffix = "") {
        let url = this.backendUrl + "/transactions" + suffix;
        url += "?" + "projectIri" + "=" + encodeURIComponent(projectIri);
        return url;
    }
    async getFullJsonModel(resource) {
        const modelData = (await (await this.httpFetch(this.getBlobUrl(resource.iri).toString())).json());
        return { resource, ...modelData };
    }
    async getModelsFromResources(resources) {
        const entityModels = [];
        const visualModels = [];
        for (const resource of resources) {
            const name = resource.userMetadata.label?.en ?? resource.userMetadata.label?.cs;
            // Visual model
            if (resource.types.includes(VISUAL_MODEL)) {
                const modelData = (await (await this.httpFetch(this.getBlobUrl(resource.iri).toString())).json());
                modelData.modelAlias = name;
                const model = createVisualModel(resource.iri).deserializeModel(modelData); // ok
                visualModels.push(model);
            }
            // SGOV model
            if (resource.types.includes(QUERYABLE_MODEL)) {
                const model = createSgovModel("https://slovník.gov.cz/sparql", this.httpFetch, resource.iri);
                const modelData = (await (await this.httpFetch(this.getBlobUrl(resource.iri).toString())).json());
                modelData.modelAlias = name;
                await model.unserializeModel(modelData);
                entityModels.push(model);
            }
            // Semantic model
            if (resource.types.includes(LOCAL_SEMANTIC_MODEL)) {
                const modelData = (await (await this.httpFetch(this.getBlobUrl(resource.iri).toString())).json());
                modelData.label ??= resource.userMetadata.label ?? {};
                const model = createInMemorySemanticModel().deserializeModel(modelData);
                entityModels.push(model);
            }
            // Pim store wrapper
            if (resource.types.includes(RDFS_MODEL)) {
                const modelData = (await (await this.httpFetch(this.getBlobUrl(resource.iri).toString())).json());
                const model = new PimStoreWrapper(modelData.pimStore, modelData.id, name, modelData.urls);
                model.fetchFromPimStore();
                entityModels.push(model);
            }
        }
        return [entityModels, visualModels];
    }
    /**
     * @deprecated Used only for importing models in cme
     */
    async getModelsFromModelDescriptors(modelDescriptors) {
        const constructedEntityModels = [];
        const constructedVisualModels = [];
        // todo: use more robust approach
        for (const modelDescriptor of modelDescriptors) {
            if (modelDescriptor.type === QUERYABLE_MODEL) {
                const model = createSgovModel("https://slovník.gov.cz/sparql", this.httpFetch);
                await model.unserializeModel(modelDescriptor);
                constructedEntityModels.push(model);
            }
            else if (modelDescriptor.type === "https://dataspecer.com/core/model-descriptor/rdfs") {
                const model = await createRdfsModel(modelDescriptor.urls, this.httpFetch);
                constructedEntityModels.push(model);
            }
            else if (modelDescriptor.type === "https://dataspecer.com/core/model-descriptor/visual-model" ||
                modelDescriptor.type === VISUAL_MODEL) {
                const model = createVisualModel(modelDescriptor.modelId).deserializeModel(modelDescriptor);
                constructedVisualModels.push(model);
            }
            else if (modelDescriptor.type === "https://dataspecer.com/core/model-descriptor/in-memory-semantic-model" ||
                modelDescriptor.type === LOCAL_SEMANTIC_MODEL) {
                const model = createInMemorySemanticModel().deserializeModel(modelDescriptor);
                constructedEntityModels.push(model);
            }
            else if (modelDescriptor.type === RDFS_MODEL) {
                const model = new PimStoreWrapper(modelDescriptor.pimStore, modelDescriptor.id, modelDescriptor.alias);
                model.fetchFromPimStore();
                constructedEntityModels.push(model);
            }
            else {
                throw new Error(`Unknown model descriptor type: ${modelDescriptor.type}. Can not create such model.`);
            }
        }
        return [constructedEntityModels, constructedVisualModels];
    }
    async copyRecursively(resourceToCopy, newParentResource, userMetadata = {}) {
        let url = this.backendUrl + "/repository/copy-recursively";
        url += "?" + "iri" + "=" + encodeURIComponent(resourceToCopy);
        url += "&" + "parentIri" + "=" + encodeURIComponent(newParentResource);
        const result = await this.httpFetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(userMetadata),
        });
        const data = await result.json();
    }
    async getResourceDataStores(iri) {
        const url = this.getResourceUrl(iri);
        const result = await this.httpFetch(url);
        if (result.status === 404) {
            return null;
        }
        const data = await result.json();
        return data.dataStores ?? null;
    }
}
//# sourceMappingURL=implementation.js.map